import { Link } from "@tanstack/react-router";
import { CarteCourses } from "./bobines-courses";
import { useBobines } from "./bobines-requetes";
import { CarteStock } from "./bobines-stock";
import {
  COMMANDE_CONNEXION,
  COMMANDE_VERIFIER,
  depuis,
  GUIDE_AGENT,
  ilYA,
  preavisJeton,
  RAISONS,
  tropAncien,
} from "./bobines-textes";
import { ALERTE_JAUNE, CARTE, Commande, FOCUS } from "./bobines-ui";
import type { RaisonEchec, VueBobines } from "./contract";
import { compter } from "./format";
import { ConsoleShell } from "./shell";
import { Chargement, EcranDErreur, panneAffichee } from "./states";

/**
 * Le module Bobines (`/bobines`, spec du 27 septembre 2026, issue #220) : le stock de filament
 * lu dans le Filament Manager de Bambu par l'agent du PC, la liste de courses en tete, le stock
 * par gamme dessous. Maquettes : `docs/maquettes/console-lab/Bobines.dc.html` (bureau),
 * `BobinesMobile.dc.html` (telephone) et `BobinesEtats.dc.html` (les etats).
 *
 * Les seules saisies sont les deux compteurs d'une reference (`bobines-compteur.tsx`) : le
 * minimum de bobines neuves, et la reserve, les neuves de l'etagere hors de l'AMS ; plus une
 * couleur du catalogue de Bambu Studio a mettre en reserve (`bobines-reserve.tsx`). Le reste
 * vient de `GET /api/bobines`, deja calcule par le backend.
 *
 * Mise en page : une colonne sous `lg`, la liste de courses d'abord ; au-dela, la liste a
 * gauche sur 380 px et le stock a droite. Le DOM suit l'ordre du telephone : le clavier et le
 * lecteur d'ecran lisent la liste de courses, puis le stock.
 */

export function BobinesPage({ maintenant = Date.now() }: { maintenant?: number }) {
  const lecture = useBobines();
  // Une relecture qui echoue (apres un minimum) garde la page, sauf une session perdue.
  const erreur = panneAffichee(lecture);
  const vue = erreur ? undefined : lecture.data;

  return (
    <ConsoleShell connecte={vue !== undefined}>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2.5">
        <div>
          <h1 className="text-[21px] font-semibold md:text-2xl">Bobines</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="hidden md:inline">
              Ton stock de filament, lu dans le Filament Manager de Bambu. Tu règles les minimums et
              la réserve, la liste de courses suit.
            </span>
            <span className="md:hidden">Ton stock, lu dans le Filament Manager de Bambu.</span>
          </p>
        </div>
        {vue ? <Puce maintenant={maintenant} vue={vue} /> : null}
      </header>
      {lecture.isPending ? <Chargement quoi="du stock" /> : null}
      {erreur ? <EcranDErreur error={lecture.error} quoi="le stock" /> : null}
      {vue && lecture.isError ? <RelectureEchouee /> : null}
      {vue ? <Contenu maintenant={maintenant} vue={vue} /> : null}
    </ConsoleShell>
  );
}

/** Une relecture ratee au-dessus de la lecture precedente, qui reste affichee. */
function RelectureEchouee() {
  return (
    <p
      className="rounded-md border border-destructive/40 bg-destructive/[0.06] px-3.5 py-2.5 text-sm text-destructive"
      role="alert"
    >
      La console n'a pas pu relire le stock : ce qui s'affiche date de la dernière lecture réussie.
    </p>
  );
}

function Contenu({ vue, maintenant }: { vue: VueBobines; maintenant: number }) {
  // Rien n'est jamais arrive et aucune reference n'est suivie : la carte du premier lancement,
  // seule (maquette `BobinesEtats.dc.html`, etat 1).
  const vide = vue.copie === null && vue.gammes.length === 0;
  return (
    <>
      <Bandeau maintenant={maintenant} vue={vue} />
      {vide ? null : (
        <div className="grid items-start gap-5 lg:grid-cols-[380px_minmax(0,1fr)] lg:gap-6">
          <CarteCourses vue={vue} />
          <CarteStock vue={vue} />
        </div>
      )}
    </>
  );
}

/**
 * La puce d'en tete : l'age de la derniere copie, quand tout va bien. L'age se compte depuis
 * `vue_le`, la derniere copie recue, meme identique (spec, section 4) : jamais depuis
 * `recue_le`, le premier jour de ce contenu, ni depuis `lue_le`, l'horloge du PC.
 */
function Puce({ vue, maintenant }: { vue: VueBobines; maintenant: number }) {
  if (vue.copie === null || echecDe(vue) !== null || tropAncien(vue.copie.vue_le, maintenant)) {
    return null;
  }
  const age = ilYA(vue.copie.vue_le, maintenant);
  const bobines = compter(vue.copie.nb_bobines, "bobine", "bobines");
  return (
    <p className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[13.5px]">
      <span aria-hidden="true" className="size-2 rounded-full bg-etape-live" />
      <span className="hidden md:inline">
        Stock lu {age}, {bobines}
      </span>
      <span className="md:hidden">
        Lu {age}, {bobines}
      </span>
    </p>
  );
}

/** Le dernier signe de l'agent, quand c'est un echec avec sa raison. */
type Echec = NonNullable<VueBobines["signe"]> & { readonly raison: RaisonEchec };

function echecDe(vue: VueBobines): Echec | null {
  const signe = vue.signe;
  if (signe === null || signe.ok || signe.raison === null) return null;
  return { ...signe, raison: signe.raison };
}

/** Les raisons ou l'encadre de l'echec dit deja de relancer la connexion. */
const RAISONS_DU_JETON: ReadonlySet<string> = new Set(["jeton_bambu_absent", "jeton_bambu_refuse"]);

/**
 * Ce qui demande un regard, dans cet ordre (plan, tache B5) : le stock jamais lu, la synchro
 * qui echoue, l'agent qui ne dit plus rien, puis, a part, le jeton Bambu qui arrive a
 * expiration. Quand tout va bien, seule la puce d'en tete parle.
 *
 * Un echec passe devant une copie trop vieille tant que son signe est recent : l'agent tourne,
 * c'est Bambu qui coince. Mais un agent qui echoue se manifeste toutes les heures : un dernier
 * signe d'echec vieux de plus de `AGE_ALERTE_JOURS` dit que l'agent ne tourne plus, et la
 * raison de ce dernier essai n'est plus que le contexte. Le preavis du jeton se tait quand
 * l'encadre de l'echec donne deja la meme commande.
 */
function Bandeau({ vue, maintenant }: { vue: VueBobines; maintenant: number }) {
  const copie = vue.copie;
  const echec = echecDe(vue);
  const echecRecent = echec !== null && !tropAncien(echec.le, maintenant) ? echec : null;
  const agentMuet =
    echecRecent === null &&
    (echec !== null || (copie !== null && tropAncien(copie.vue_le, maintenant)));
  const jetonEnCause = echecRecent !== null && RAISONS_DU_JETON.has(echecRecent.raison);
  const preavis = jetonEnCause ? null : preavisJeton(vue.jeton_expire_le, maintenant);
  return (
    <>
      {copie === null ? <JamaisLu /> : null}
      {echecRecent !== null ? (
        <EchecDeLaSynchro copie={copie} echec={echecRecent} maintenant={maintenant} />
      ) : agentMuet ? (
        <AgentMuet copie={copie} dernierEchec={echec} maintenant={maintenant} />
      ) : null}
      {preavis === null ? null : (
        <p className={ALERTE_JAUNE}>
          <span>
            {preavis.expire
              ? `Ton jeton Bambu a expiré le ${preavis.date} : relance `
              : `Ton jeton Bambu expire le ${preavis.date} : relance `}
            <Commande>{COMMANDE_CONNEXION}</Commande>
            {preavis.expire ? "." : " avant."}
          </span>
        </p>
      )}
    </>
  );
}

/** La synchro qui echoue : la raison en clair, ce qu'il faut faire, ce que l'agent precise. */
function EchecDeLaSynchro({
  echec,
  copie,
  maintenant,
}: {
  echec: Echec;
  copie: VueBobines["copie"];
  maintenant: number;
}) {
  const raison = RAISONS[echec.raison];
  return (
    <div className="flex flex-col gap-2">
      <div className={ALERTE_JAUNE}>
        <p>
          <b className="font-semibold">
            La synchro échoue depuis {depuis(echec.echec_depuis ?? echec.le, maintenant)} :
          </b>{" "}
          {raison.quoi}
        </p>
        <p className="text-[13.5px]">
          {raison.geste.avant}
          {raison.geste.commande === null ? null : (
            <>
              {" "}
              <Commande>{raison.geste.commande}</Commande>
              {raison.geste.apres === "" ? null : ` ${raison.geste.apres}`}
            </>
          )}
        </p>
        {echec.detail ? (
          // Le texte de l'agent, jusqu'a 300 caracteres : une adresse sans espace ne doit pas
          // deborder de l'ecran du telephone. En `--foreground` sur l'encadre jaune, comme le
          // reste de l'encadre (le gris y tombait vers 4,7:1).
          <p className="text-[13px] [overflow-wrap:anywhere]">L'agent précise : {echec.detail}</p>
        ) : null}
      </div>
      {copie === null ? null : (
        <p className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <span aria-hidden="true" className="size-2 flex-none rounded-full bg-racheter" />
          Stock lu {ilYA(copie.vue_le, maintenant)} : c'est la dernière bonne copie qui s'affiche.
        </p>
      )}
    </div>
  );
}

/**
 * L'agent ne dit plus rien depuis plus de `AGE_ALERTE_JOURS` : sa copie est trop vieille, ou son
 * dernier signe est un echec trop vieux, dont la raison reste en contexte.
 */
function AgentMuet({
  copie,
  dernierEchec,
  maintenant,
}: {
  copie: VueBobines["copie"];
  dernierEchec: Echec | null;
  maintenant: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className={ALERTE_JAUNE}>
        <p>
          {copie === null ? null : (
            <>
              <b className="font-semibold">Stock lu {ilYA(copie.vue_le, maintenant)}.</b>{" "}
            </>
          )}
          L'agent du PC ne tourne plus ?
        </p>
        <p className="text-[13.5px]">
          PC éteint, ou tâche planifiée arrêtée. Sur le PC, <Commande>{COMMANDE_VERIFIER}</Commande>{" "}
          dit ce qui coince.
        </p>
        {dernierEchec === null ? null : (
          <p className="text-[13px]">
            Dernier essai {ilYA(dernierEchec.le, maintenant)} : {RAISONS[dernierEchec.raison].quoi}
          </p>
        )}
        <LienGuide nom={GUIDE_AGENT}>Guide de l'agent</LienGuide>
      </div>
      {copie === null ? null : (
        <p className="text-[13px] text-muted-foreground">
          La liste de courses reste celle de la dernière copie.
        </p>
      )}
    </div>
  );
}

/** Le premier lancement : aucune copie n'est encore arrivee (maquette, etat 1). */
function JamaisLu() {
  return (
    <section aria-labelledby="bobines-jamais-lu" className={`${CARTE} flex flex-col gap-2.5`}>
      <h2 className="text-[15px] font-semibold" id="bobines-jamais-lu">
        Le stock n'a jamais été lu
      </h2>
      <p className="text-sm">
        L'agent de ton PC lit le Filament Manager de Bambu et envoie une copie ici toutes les
        heures. Il n'a encore rien envoyé.
      </p>
      <p className="text-sm">
        Prochaine étape : le brancher depuis le PC. Tu le vérifies, tu lances une première synchro,
        puis tu poses la tâche planifiée.
      </p>
      <LienGuide nom={GUIDE_AGENT}>Guide : brancher l'agent des bobines</LienGuide>
    </section>
  );
}

/** Un guide du PC, lu dans la console comme tout geste hors idee. */
function LienGuide({ nom, children }: { nom: string; children: string }) {
  return (
    <p>
      <Link
        className={`rounded-sm text-[13.5px] font-medium underline underline-offset-[3px] ${FOCUS}`}
        params={{ nom }}
        to="/portefeuille/gestes/$nom"
      >
        {children}
      </Link>
    </p>
  );
}
